import React, { useState } from 'react';
import { XMarkIcon, RocketLaunchIcon, CheckCircleIcon, WrenchScrewdriverIcon, SparklesIcon } from '@heroicons/react/24/outline';

// 当前版本号
export const APP_VERSION = 'v2.2.1';

// 变更类型
type ChangeType = 'feature' | 'fix' | 'improve' | 'other';

interface ChangeItem {
  type: ChangeType;
  text: string;
}

interface VersionEntry {
  version: string;
  date: string;
  title: string;
  changes: ChangeItem[];
}

// ====== 版本记录数据 ======
// 每次封版时在此数组头部添加新版本记录
const CHANGELOG: VersionEntry[] = [
  {
    version: 'v2.2.1',
    date: '2026-04-15',
    title: '导出格式与目录编号优化',
    changes: [
      { type: 'feature', text: '新增全局内容首行缩进功能' },
      { type: 'fix', text: '修复导出 Word 首行缩进不生效的问题' },
      { type: 'fix', text: '修复导出 Word 有序列表序号未正确延续的问题' },
      { type: 'fix', text: '修复目录格式选择后未正确保存的问题' },
      { type: 'fix', text: '修复导出表格时 Markdown 分隔行被误输出的问题' },
    ],
  },
  {
    version: 'v2.2.0',
    date: '2026-04-14',
    title: '发布优化与代码清理',
    changes: [
      { type: 'improve', text: '模版提取准确性优化，提升解析成功率' },
      { type: 'improve', text: '翻译功能异常处理增强' },
      { type: 'improve', text: '招标文件删除逻辑优化' },
      { type: 'fix', text: '修复其它资料上传后不显示及删除无效的问题' },
      { type: 'other', text: '清理冗余代码与调试日志' },
    ],
  },
  {
    version: 'v2.1.0',
    date: '2026-04-10',
    title: '智能报价与全局生成设置',
    changes: [
      { type: 'feature', text: '新增报价模块智能报价功能' },
      { type: 'feature', text: '文件生成支持全局参数设置' },
      { type: 'improve', text: '优化企业主体信息管理与退出校验逻辑' },
      { type: 'improve', text: '提升文件上传与处理效率' },
      { type: 'improve', text: '优化模型生成稳定性' },
      { type: 'fix', text: '修复部分章节生成失败的问题' },
    ],
  },
  {
    version: 'v2.0.0',
    date: '2026-04-02',
    title: '基础设施优化',
    changes: [
      { type: 'improve', text: '数据库连接配置优化，消除硬编码依赖' },
      { type: 'improve', text: '服务端用户真实 IP 获取逻辑修正' },
    ],
  },
  {
    version: 'v1.9.0',
    date: '2026-03-23',
    title: '视频教程与登录体验优化',
    changes: [
      { type: 'feature', text: '首页新增视频教程入口与配置管理' },
      { type: 'feature', text: '新用户注册赠送额度提示功能' },
      { type: 'improve', text: '优化系统弹框提示交互方式' },
      { type: 'improve', text: '完善投标主体信息更新流程' },
      { type: 'improve', text: '登录配置与上线前稳定性优化' },
    ],
  },
  {
    version: 'v1.8.0',
    date: '2026-03-22',
    title: 'Token 欠费管理与双环境部署',
    changes: [
      { type: 'feature', text: 'Token 欠费检查和充值提示功能' },
      { type: 'feature', text: '创建双环境（标准版/企业版）编译部署脚本' },
      { type: 'improve', text: '招标解析、目录生成和文件生成提示框优化' },
      { type: 'fix', text: '修复欠费未停机后不记录 Token 消耗的问题' },
      { type: 'fix', text: '修复目录生成的欠费提示逻辑' },
    ],
  },
  {
    version: 'v1.7.0',
    date: '2026-03-16',
    title: '微信支付生产上线',
    changes: [
      { type: 'feature', text: '微信登录和微信支付生产环境全流程打通' },
      { type: 'improve', text: '微调微信扫码登录和飞书 Logo 显示' },
      { type: 'improve', text: '优化首页渐变色和登录页文字颜色' },
    ],
  },
  {
    version: 'v1.6.0',
    date: '2026-03-15',
    title: '充值与邀请系统',
    changes: [
      { type: 'feature', text: '添加微信扫码登录和在线充值功能' },
      { type: 'feature', text: '添加邀请相关功能逻辑' },
      { type: 'feature', text: '导出样式支持 4-6 级目录' },
      { type: 'improve', text: '主体信息归创建人所有，复制项目自动复制主体' },
      { type: 'improve', text: '去掉项目类型字段，优化上线准备' },
      { type: 'other', text: '清理未使用的测试文件' },
    ],
  },
  {
    version: 'v1.5.0',
    date: '2026-03-09',
    title: '6级目录与安全加固',
    changes: [
      { type: 'feature', text: '支持 6 级目录结构和目录编辑' },
      { type: 'improve', text: '模型 API Key 仅存储在后端，提升安全性' },
      { type: 'fix', text: '修复批注重叠压盖问题' },
    ],
  },
  {
    version: 'v1.4.0',
    date: '2026-03-07',
    title: '页面布局重构与体验优化',
    changes: [
      { type: 'improve', text: '统一管理页左侧加载等待效果' },
      { type: 'improve', text: '优化步骤退出自动保存和快速页退出不提示' },
      { type: 'improve', text: '章节关联资源改为弹框交互，优化置顶按钮位置' },
      { type: 'improve', text: '重构文件生成页面布局效果' },
      { type: 'improve', text: '调整左侧步骤面板和批注显示' },
      { type: 'improve', text: '优化检查助手显示和资源弹框宽度' },
    ],
  },
  {
    version: 'v1.3.0',
    date: '2026-03-05',
    title: '多厂商模型与项目别名',
    changes: [
      { type: 'feature', text: '优化 5 个厂商的模型接入，支持无思考快速生成' },
      { type: 'feature', text: '增加项目别名功能（项目配置、项目管理）' },
      { type: 'improve', text: '优化文件生成过程效果和逻辑' },
      { type: 'fix', text: '修复导出流程图显示异常的问题' },
    ],
  },
  {
    version: 'v1.2.0',
    date: '2026-03-03',
    title: '权限管理与项目组协作',
    changes: [
      { type: 'feature', text: '增加用户三级权限管理（超管/管理员/普通用户）' },
      { type: 'feature', text: '添加人员管理和充值功能' },
      { type: 'feature', text: '增加项目组管理功能' },
      { type: 'feature', text: '团队成员可查看项目组项目并复制' },
      { type: 'feature', text: '添加项目角标标识' },
      { type: 'improve', text: '优化目录样式修改、目录字数恢复显示' },
      { type: 'improve', text: '优化左侧菜单效果，改为浅色风格' },
      { type: 'improve', text: '优化模型选择交互' },
      { type: 'fix', text: '修复智能插入表格、流程图的请求授权问题' },
      { type: 'fix', text: '修复预览模式不显示图片问题' },
      { type: 'fix', text: '解决导出 Word 图片排版错位问题' },
    ],
  },
  {
    version: 'v1.1.0',
    date: '2026-03-02',
    title: '飞书三方登录集成',
    changes: [
      { type: 'feature', text: '集成飞书 OAuth 登录功能' },
      { type: 'feature', text: '添加飞书登录路由和回调处理逻辑' },
      { type: 'improve', text: 'Login 页面添加飞书登录入口和蓝白渐变效果' },
      { type: 'fix', text: '修复飞书 OAuth Token 获取接口认证方式错误' },
      { type: 'fix', text: '修复后端 config.py 缺少飞书 OAuth 配置字段导致启动失败' },
    ],
  },
  {
    version: 'v1.0.0',
    date: '2026-03-01',
    title: '首个正式发布版本',
    changes: [
      { type: 'feature', text: '完整的标书生成五步流程：上传文件 → 智能解析 → 目录管理 → 内容生成 → 导出Word' },
      { type: 'feature', text: '模型 Token 用量统计与管理' },
      { type: 'improve', text: '优化首页内容展示' },
      { type: 'improve', text: '优化文件生成里的生成逻辑及停止功能' },
      { type: 'improve', text: '去掉左侧顶部副标题，界面更简洁' },
    ],
  },
  {
    version: 'v0.8.0',
    date: '2026-02-28',
    title: '智能助手与导出优化',
    changes: [
      { type: 'feature', text: '智能助手完成流程数据检查、章节内容检查、招标评分检查' },
      { type: 'feature', text: '文件生成添加样式设置：章节分页、头部文件名' },
      { type: 'feature', text: '增加分页符支持' },
      { type: 'improve', text: '导出样式设置保存到数据库' },
      { type: 'improve', text: '优化目录显示和导出效果（行间距、首页标题、下划线）' },
      { type: 'improve', text: '优化图片插入交互' },
      { type: 'improve', text: '智能助手面板简洁优化' },
      { type: 'fix', text: '修复招标解析模版提取格式的问题' },
      { type: 'fix', text: '解决目录修改字数不同步问题' },
      { type: 'fix', text: '优化分项报价兼容性问题' },
    ],
  },
  {
    version: 'v0.7.0',
    date: '2026-02-25',
    title: '资源关联生成与字数管理',
    changes: [
      { type: 'feature', text: '添加基于资源关联的内容生成能力' },
      { type: 'feature', text: '添加字数管理（目录生成和文件生成对章节字数管理）' },
      { type: 'improve', text: '优化资源的自动关联能力' },
      { type: 'improve', text: '优化招标解析提取项目名称和项目管理时间' },
      { type: 'improve', text: '目录生成优化' },
    ],
  },
  {
    version: 'v0.6.0',
    date: '2026-02-20',
    title: '资源关联与招标模版',
    changes: [
      { type: 'feature', text: '添加章节内容资源关联面板' },
      { type: 'feature', text: '添加招标模版解析功能' },
      { type: 'feature', text: '插入流程图添加架构图选项' },
      { type: 'feature', text: '生成资源关联支持招标模版' },
      { type: 'improve', text: '优化选中生成流程图和段落智能优化按钮位置' },
    ],
  },
  {
    version: 'v0.5.0',
    date: '2026-02-14',
    title: '内容编辑增强',
    changes: [
      { type: 'feature', text: '添加内容设置弹框' },
      { type: 'feature', text: '右键插入图片弹框功能' },
      { type: 'improve', text: '注记跟随和注记比例优化' },
      { type: 'improve', text: '智能助手支持任意拖动和变成小球' },
      { type: 'improve', text: '优化批注面板控制和显示' },
      { type: 'improve', text: '优化返回头部箭头按钮' },
      { type: 'improve', text: '修改内容插入表格弹框效果' },
      { type: 'improve', text: '修改导出样式' },
    ],
  },
  {
    version: 'v0.4.0',
    date: '2026-02-13',
    title: '管理功能完善',
    changes: [
      { type: 'feature', text: '完成主体管理、系统设置、模型默认设置等功能' },
      { type: 'feature', text: '增加自定义目录格式' },
      { type: 'feature', text: '文件生成内容添加注记功能' },
      { type: 'improve', text: '快速启动页优化' },
      { type: 'improve', text: '优化文件生成内容注记' },
    ],
  },
  {
    version: 'v0.3.0',
    date: '2026-02-11',
    title: '在线化与用户认证',
    changes: [
      { type: 'feature', text: '本地存储改为线上存储，四个步骤和项目管理全流程走通' },
      { type: 'feature', text: '增加登录注册鉴权功能' },
      { type: 'feature', text: '增加招标解析的分项报价' },
      { type: 'feature', text: '各模块添加路由，添加 Home 页' },
      { type: 'improve', text: '优化项目管理页面布局和效果' },
      { type: 'improve', text: '增加代理环境支持' },
      { type: 'fix', text: '修复无登录进入详情不弹出登录框的问题' },
      { type: 'fix', text: '修复左侧展开和收缩错位问题' },
    ],
  },
  {
    version: 'v0.2.0',
    date: '2026-02-08',
    title: '内容编辑与导出',
    changes: [
      { type: 'feature', text: '生成内容编辑器升级，支持居左居右' },
      { type: 'feature', text: '生成内容导出 Word 格式保持一致' },
      { type: 'feature', text: '增加流程图和邮件表格插入功能' },
      { type: 'feature', text: '文件生成增加生成失败的目录状态' },
      { type: 'feature', text: '标书解析增加废标项 tab' },
      { type: 'improve', text: '步骤显示改为左侧显示' },
      { type: 'improve', text: '优化文件生成页布局和目录跟随效果' },
      { type: 'improve', text: '初步增加段落智能优化功能' },
      { type: 'fix', text: '修复导出出现 &lt;br&gt; 标签的 bug' },
      { type: 'fix', text: '解决失败个数没有同步到头部数量及表格超出问题' },
    ],
  },
  {
    version: 'v0.1.0',
    date: '2026-02-06',
    title: '核心功能搭建',
    changes: [
      { type: 'feature', text: '项目配置页面提取' },
      { type: 'feature', text: '项目生成页左侧添加目录，支持点击显示章节信息' },
      { type: 'feature', text: '文件解析支持表格显示和导出' },
      { type: 'feature', text: '投标解析支持复制、编辑、重新生成' },
      { type: 'feature', text: '目录生成页格式修改和删除确认' },
      { type: 'feature', text: '标书章节单独生成和单独编辑功能' },
      { type: 'improve', text: '调整项目结构，左侧增加各功能入口' },
      { type: 'improve', text: '优化大模型的选择，移至项目配置' },
    ],
  },
  {
    version: 'v0.0.1',
    date: '2026-02-05',
    title: '项目初始化',
    changes: [
      { type: 'feature', text: '项目初始提交' },
      { type: 'feature', text: '添加招标文件目录提取，并按目录生成' },
    ],
  },
];

const typeConfig: Record<ChangeType, { icon: React.ElementType; label: string; color: string; bg: string }> = {
  feature: { icon: SparklesIcon, label: '新功能', color: 'text-blue-600', bg: 'bg-blue-50' },
  fix: { icon: WrenchScrewdriverIcon, label: '修复', color: 'text-orange-600', bg: 'bg-orange-50' },
  improve: { icon: CheckCircleIcon, label: '优化', color: 'text-green-600', bg: 'bg-green-50' },
  other: { icon: RocketLaunchIcon, label: '其他', color: 'text-gray-600', bg: 'bg-gray-50' },
};

interface ChangelogDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ChangelogDialog: React.FC<ChangelogDialogProps> = ({ isOpen, onClose }) => {
  const [expandedVersion, setExpandedVersion] = useState<string | null>(CHANGELOG[0]?.version || null);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* 遮罩层 */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* 弹框 */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-[560px] max-h-[80vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* 头部 */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md">
              <RocketLaunchIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-800">版本更新记录</h2>
              <p className="text-xs text-gray-400">当前版本 {APP_VERSION}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* 版本列表 */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {CHANGELOG.map((entry, index) => {
            const isExpanded = expandedVersion === entry.version;
            const isLatest = index === 0;

            return (
              <div key={entry.version} className="relative">
                {/* 版本头 */}
                <button
                  onClick={() => setExpandedVersion(isExpanded ? null : entry.version)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left"
                >
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isLatest ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-800">{entry.version}</span>
                      {isLatest && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">最新</span>
                      )}
                      <span className="text-xs text-gray-400">{entry.date}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{entry.title}</p>
                  </div>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* 变更详情 */}
                {isExpanded && (
                  <div className="mt-2 ml-5 pl-4 border-l-2 border-gray-100 space-y-1.5 pb-2">
                    {entry.changes.map((change, i) => {
                      const cfg = typeConfig[change.type];
                      const Icon = cfg.icon;
                      return (
                        <div key={i} className="flex items-start gap-2 py-1">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color} flex-shrink-0 mt-0.5`}>
                            <Icon className="w-3 h-3" />
                            {cfg.label}
                          </span>
                          <span className="text-xs text-gray-600 leading-relaxed">{change.text}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* 底部 */}
        <div className="px-6 py-3 border-t border-gray-100 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangelogDialog;
